import React from "react";
import { Link, useHistory } from "react-router-dom";
import clsx from "clsx";

import { ReactComponent as GroupIcon } from "../../assets/svg/discussion.svg";
import { ReactComponent as AnswersOtherIcon } from "../../assets/svg/answers_other_2.svg";
import { ReactComponent as SaveIcon } from "../../assets/svg/answerer_2.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit_2.svg";
import { ReactComponent as QuestionnaireIcon } from "../../assets/svg/edit_questionnaire.svg";
import { groupPath } from "../../studentStore";
import { ILesson } from "../../state";
import DropdownMenu from "../../atoms/DropdownMenu";

import styles from "./MaterialSidebar.module.scss";

export interface MaterialSidebarProps {
  hasIssue: boolean;
  isChangeAnswer: boolean;
  isEditMode: boolean;
  calling: boolean;
  lesson: ILesson;
  roundNo: number;
  currentPage: number;
  onSubmit: () => void;
  onResetAnswer: () => void;
  onSaveToStorage: () => void;
  onStartEditing: () => void;
  onShowModalAnswers: () => void;
  onShowModalEvaluate: () => void;
}

export const MaterialSidebar: React.FC<MaterialSidebarProps> = ({
  hasIssue,
  isChangeAnswer,
  isEditMode,
  calling,
  lesson,
  roundNo,
  currentPage,
  onSubmit,
  onResetAnswer,
  onSaveToStorage,
  onStartEditing,
  onShowModalAnswers,
  onShowModalEvaluate,
}) => {
  const history = useHistory();

  const showEvaluateButton = !!lesson.student_lesson_retrospective;

  const roundOption =
    lesson.publish_round_nos?.map(round_no => ({
      label: `シート(${round_no})`,
      value: round_no,
    })) ?? [];

  const handleChangeRoundNo = (roundNo: number) => {
    const roundNoParam = roundNo > 1 ? `&round=${roundNo}` : "";
    history.push({
      search: `?teachingMaterial=${lesson.teaching_material_id}&lesson=${lesson.id}${roundNoParam}&page=1`,
    });
  };

  return isEditMode ? (
    <div key="edit" className={styles.wrapper}>
      <button
        onClick={onSubmit}
        className={clsx(styles.actionBtn, styles.primary)}
        disabled={!hasIssue || !isChangeAnswer || calling}
      >
        <SaveIcon className={styles.icon} />
        <span>
          ワーク
          <br />
          回答送信
        </span>
      </button>
      {showEvaluateButton && (
        <div
          className={clsx(styles.actionBtn, styles.hidden)}
          aria-hidden="true"
        />
      )}
      <button
        onClick={onResetAnswer}
        className={clsx(
          styles.actionBtn,
          styles.secondary,
          !isChangeAnswer && styles.hidden,
        )}
      >
        <span>
          下書きを
          <br />
          削除して終了
        </span>
      </button>
      <button
        onClick={onSaveToStorage}
        className={clsx(styles.actionBtn, styles.secondary)}
      >
        <span>
          {isChangeAnswer ? (
            <>
              下書きを
              <br />
              保存して終了
            </>
          ) : (
            <>編集を終了</>
          )}
        </span>
      </button>
    </div>
  ) : (
    <div key="normal" className={styles.wrapper}>
      {lesson.publish_round_nos && lesson.publish_round_nos.length > 1 && (
        <DropdownMenu
          className={styles.dropdown}
          size="sm"
          options={roundOption}
          defaultValue={roundOption.find(o => o.value === roundNo)}
          openUpwards
          onChange={value => {
            handleChangeRoundNo(value);
          }}
        />
      )}
      <button
        onClick={onStartEditing}
        className={clsx(
          styles.actionBtn,
          isChangeAnswer ? styles.alert : styles.primary,
        )}
        disabled={!hasIssue}
      >
        <EditIcon className={styles.icon} />
        <span>
          {isChangeAnswer ? (
            <>
              下書きの
              <br />
              編集を再開
            </>
          ) : (
            <>
              ワーク
              <br />
              回答入力
            </>
          )}
        </span>
      </button>
      <button
        onClick={onShowModalAnswers}
        className={clsx(styles.actionBtn, styles.primary)}
        disabled={!hasIssue}
      >
        <AnswersOtherIcon className={styles.icon} />
        <span>みんなの回答</span>
      </button>
      <div className={clsx(styles.linkBtnWrapper)}>
        <Link
          to={{
            pathname: groupPath,
            state: {
              materialId: lesson.teaching_material_id,
              lessonId: lesson.id,
              page: currentPage,
              roundNo,
            },
          }}
          className={clsx(styles.actionBtn, styles.primary)}
        >
          <GroupIcon className={styles.icon} />
          <span>グループ</span>
        </Link>
      </div>
      {showEvaluateButton && (
        <button
          onClick={onShowModalEvaluate}
          className={clsx(styles.actionBtn, styles.primary)}
        >
          <QuestionnaireIcon className={styles.icon} />
          <span>アンケート編集</span>
        </button>
      )}
    </div>
  );
};

export default MaterialSidebar;
